// ─── Status Page — what a subscriber's system selection MEANS (b-02007) ──────
// ONE definition of the tri-state systems checkbox, used by BOTH the public
// subscribe/manage SystemPicker and the admin "Edit subscriber" sheet. Those two
// drew the same stored ids differently before, and neither drew them the way the
// fan-out reads them — which is how a subscriber with "Classic" stored showed up
// with Classic ticked and every system under it EMPTY, while in fact receiving
// all of them.
//
// ── THE RULE IS THE FAN-OUT'S ──
// A stored id stands for its WHOLE subtree: expandComponentScope (api,
// services/status/subscribers.ts) widens an incident's systems to ancestors AND
// descendants, so a subscriber holding "Classic" hears about Classic › e-nettet ›
// anything, including systems added under it after they signed up. The display
// therefore reads the same way:
//   • checked   — the system, or one of its ancestors, is stored; or every one of
//                 its children is checked.
//   • partial   — not checked, but something beneath it is.
//   • unchecked — nothing in its subtree is.
// Storage is unchanged and no migration was needed: rows that hold only a parent
// (6 Aug signups, anything saved through the old flat admin list) already MEANT
// the whole branch, and now simply look like it.
//
// ── WHAT GETS STORED ──
// The smallest set that says the same thing: a fully checked branch is stored as
// its top node, a partial one as the checked nodes beneath it. Storing the
// parent (not the parent plus today's children) is what keeps a future child
// covered. Ids that are not in the tree — an archived system the picker was not
// given — are passed through untouched; dropping them would silently widen or
// narrow a feed the operator never looked at.
//
// Pure and dependency-free (this package compiles with `lib: ES2022` only), and
// cycle-safe: a hand-edited parent loop must not hang a browser tab.
function indexTree(nodes) {
    const ids = new Set(nodes.map((n) => n.id));
    const roots = [];
    const childrenOf = new Map();
    const parentOf = new Map();
    for (const n of nodes) {
        const parent = n.parentId && ids.has(n.parentId) && n.parentId !== n.id ? n.parentId : null;
        parentOf.set(n.id, parent);
        if (!parent) {
            roots.push(n.id);
            continue;
        }
        const list = childrenOf.get(parent);
        if (list)
            list.push(n.id);
        else
            childrenOf.set(parent, [n.id]);
    }
    return { roots, childrenOf, parentOf };
}
function subtreeOf(id, index) {
    const out = [];
    const seen = new Set();
    const stack = [id];
    while (stack.length) {
        const next = stack.pop();
        if (seen.has(next))
            continue;
        seen.add(next);
        out.push(next);
        stack.push(...(index.childrenOf.get(next) ?? []));
    }
    return out;
}
function ancestorsOf(id, index) {
    const out = [];
    const seen = new Set([id]);
    let cursor = index.parentOf.get(id) ?? null;
    while (cursor && !seen.has(cursor)) {
        seen.add(cursor);
        out.push(cursor);
        cursor = index.parentOf.get(cursor) ?? null;
    }
    return out;
}
/** Every node that renders CHECKED: each stored id's whole subtree, plus — bottom
 *  up — any parent all of whose children are checked. */
function fullyChecked(selected, index) {
    const checked = new Set();
    for (const id of selected) {
        if (!index.parentOf.has(id))
            continue;
        for (const d of subtreeOf(id, index))
            checked.add(d);
    }
    const settle = (id, trail) => {
        if (checked.has(id))
            return true;
        if (trail.has(id))
            return false;
        trail.add(id);
        const children = index.childrenOf.get(id) ?? [];
        const results = children.map((c) => settle(c, trail));
        const all = children.length > 0 && results.every(Boolean);
        if (all)
            checked.add(id);
        return all;
    };
    for (const root of index.roots)
        settle(root, new Set());
    return checked;
}
/** Tri-state per system, for `checked` + `indeterminate` on the checkbox. */
export function systemCheckStates(nodes, selected) {
    const index = indexTree(nodes);
    const checked = fullyChecked(selected, index);
    const states = new Map();
    const resolve = (id, trail) => {
        const known = states.get(id);
        if (known)
            return known;
        if (trail.has(id))
            return 'unchecked';
        trail.add(id);
        let state = 'unchecked';
        if (checked.has(id))
            state = 'checked';
        else if ((index.childrenOf.get(id) ?? []).map((c) => resolve(c, trail)).some((s) => s !== 'unchecked'))
            state = 'partial';
        states.set(id, state);
        return state;
    };
    for (const n of nodes)
        resolve(n.id, new Set());
    return states;
}
/** The canonical stored form of a selection — see WHAT GETS STORED above. A
 *  legacy "parent plus some children" row collapses to just the parent, which
 *  is what the fan-out already treated it as. */
export function normalizeSystemSelection(nodes, selected) {
    const index = indexTree(nodes);
    return compact(fullyChecked(selected, index), selected, index);
}
function compact(checked, selected, index) {
    const out = [];
    const seen = new Set();
    const walk = (id) => {
        if (seen.has(id))
            return;
        seen.add(id);
        if (checked.has(id)) {
            out.push(id);
            return;
        }
        for (const c of index.childrenOf.get(id) ?? [])
            walk(c);
    };
    for (const root of index.roots)
        walk(root);
    const outside = selected.filter((id) => !index.parentOf.has(id));
    return [...out, ...Array.from(new Set(outside))];
}
/**
 * One click on one checkbox, returned as the WHOLE next selection.
 *
 * ── TICKING A PARENT TAKES ITS WHOLE SUBTREE WITH IT ──
 * The children sit indented beneath it, so selecting the group reads as
 * selecting the group — and it is stored as the parent alone, so a system added
 * under it later is covered too. Ticking a PARTIAL parent completes it.
 *
 * ── UNTICKING A PARENT CLEARS ITS SUBTREE ──
 * Same reason in reverse: a click that only removed the parent would leave its
 * children subscribed and the group looking deselected.
 *
 * ── UNTICKING A CHILD REMOVES THAT CHILD, AND ONLY IT ──
 * Its siblings stay checked and the parent turns PARTIAL. The parent can no
 * longer stay stored: a stored parent means its whole subtree to the fan-out,
 * so the child just unticked would keep arriving — the manual removal would be
 * a lie. (That is exactly what the picker did before b-02007.) The subscriber
 * still hears about the parent's OWN incidents: an incident on "Classic" widens
 * down to the siblings they kept.
 *
 * ── COMPLETING A BRANCH FOLDS IT BACK ──
 * Re-ticking the last missing child turns the parent checked again and stores
 * the parent, not the list of children.
 */
export function toggleSystemSelection(nodes, selected, id) {
    const index = indexTree(nodes);
    if (!index.parentOf.has(id))
        return normalizeSystemSelection(nodes, selected);
    const checked = fullyChecked(selected, index);
    if (checked.has(id)) {
        for (const d of subtreeOf(id, index))
            checked.delete(d);
        for (const a of ancestorsOf(id, index))
            checked.delete(a);
    }
    else {
        for (const d of subtreeOf(id, index))
            checked.add(d);
    }
    return compact(fullyChecked(compact(checked, selected, index), index), selected, index);
}
/** How many systems a selection covers, descendants of a stored parent
 *  included — the number a "N valgt" / "Only these N systems" line must show.
 *  Counting stored ids instead would call a whole "Classic" branch "1 system". */
export function countCheckedSystems(nodes, selected) {
    let n = 0;
    for (const state of systemCheckStates(nodes, selected).values())
        if (state === 'checked')
            n += 1;
    return n;
}
//# sourceMappingURL=status-system-selection.js.map